/// <reference types="vite/client" />
import * as dialog from '@zag-js/dialog';
import { normalizeProps, useMachine } from '@zag-js/solid';
import {
  DataProvider,
  EventBusProvider,
  Icon,
  Node,
  setSpecValidateOnPatch,
  spec,
  specPatch,
  specUpdate,
  ThemeProvider,
  useEventBus,
  useTheme,
} from 'framework';
import { assertRootNotDerived, DerivedResolutionError } from './derive';
import { panelsOpenSignature } from './subscription-signatures';
import {
  resolvePanelAlignment,
  resolvePanelContentSize,
  resolvePanelMaxSize,
} from './panel-alignment';
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { Portal } from 'solid-js/web';
import {
  createLogger,
  matchesAnyTrigger as matchesAnyEventTrigger,
} from 'utils';
import classes from './renderer.module.css';

// EID-64-style debug gate: postMessage payloads (full spec/patch) shouldn't
// hit the console unconditionally in production. Enable with
// `localStorage.debug = 'eidos:renderer'`.
const log = createLogger('renderer');

const HeaderBar = (props) => {
  const theme = useTheme();
  return (
    <div class={classes.topbar}>
      <Show when={props.logo}>
        <img src={props.logo} class={classes.logo} />
      </Show>
      <Show when={props.title}>
        <h1 class={classes.title}>{props.title}</h1>
      </Show>
    </div>
  );
};

const matchesAnyTrigger = (event, triggers) => {
  if (!event || !triggers) return false;
  // unwrap the Solid store proxy before matching; matchesAnyEventTrigger
  // short-circuits on event.action before the deep compareObjects (EID-64).
  return matchesAnyEventTrigger(event, unwrap(triggers));
};

const PanelNode = (props) => {
  const theme = useTheme();
  const { triggerEvent } = useEventBus();
  const service = useMachine(dialog.machine, {
    id: createUniqueId(),
    modal: true,
    defaultOpen: false,
    onOpenChange(details) {
      if (details.open) props.onOpen();
      else props.onClose();

      triggerEvent({
        action: details.open ? 'open' : 'close',
        source: `/panels/${props.id}`,
        data: null,
      });
    },
  });
  const api = createMemo(() => dialog.connect(service, normalizeProps));
  createEffect(
    on(
      () => props.open,
      () => {
        if (props.open !== api().open) api().setOpen(props.open);
      },
    ),
  );

  // EID-103 R7: derived memos (not one-shot `const`s computed at setup) so
  // patching props.position after mount updates the flex alignment.
  const alignment = createMemo(() => resolvePanelAlignment(props.position));
  const maxSize = createMemo(() => resolvePanelMaxSize(props.position));

  // Measured header height feeds the content-size floor: content keeps the
  // spec size (minus header) inside the clamped frame so an oversized panel
  // pans instead of squashing its fit-to-container content. rAF defers the
  // read until after layout (same pattern as Node's initial measurement).
  const [headerHeight, setHeaderHeight] = createSignal(0);
  const measureHeader = (el) =>
    requestAnimationFrame(() => setHeaderHeight(el?.offsetHeight ?? 0));
  createEffect(() => {
    // Header removed at runtime (title unset while closable is false) —
    // the ref never re-fires on unmount, so reset explicitly.
    if (!(props.title || props.closable !== false)) setHeaderHeight(0);
  });
  const contentSize = createMemo(() =>
    resolvePanelContentSize(props.width, props.height, headerHeight()),
  );

  return (
    <Show when={api().open}>
      <Portal mount={document.querySelector('#eidos')}>
        <div {...api().getBackdropProps()} />
        <div
          {...api().getPositionerProps()}
          style={{
            ...api().getPositionerProps().style,
            position: 'absolute',
            'align-items': alignment().align,
            'justify-content': alignment().justify,
          }}
        >
          <div
            {...api().getContentProps()}
            style={{
              ...api().getContentProps().style,
              height: props.height
                ? typeof props.height === 'number'
                  ? `${props.height}px`
                  : props.height
                : '80%',
              width: props.width
                ? typeof props.width === 'number'
                  ? `${props.width}px`
                  : props.width
                : '80%',
              // Spec sizes are preferred sizes: clamp to the viewport-sized
              // positioner (minus pinned offsets) so an oversized panel scrolls
              // internally instead of being clipped off-screen unreachable.
              'max-width': maxSize().maxWidth,
              'max-height': maxSize().maxHeight,
              'z-index': 100,
              ...(props.position && {
                position: 'absolute',
                ...Object.fromEntries(
                  Object.entries(props.position)
                    .filter(([, v]) => v != null)
                    .map(([k, v]) => [k, typeof v === 'number' ? `${v}px` : v]),
                ),
              }),
            }}
          >
            <Show when={props.title || props.closable !== false}>
              <div class={classes.panelHeader} ref={measureHeader}>
                <Show when={props.title}>
                  <span {...api().getTitleProps()} class={classes.panelTitle}>
                    {props.title}
                  </span>
                </Show>
                <Show when={props.closable !== false}>
                  <button
                    {...api().getCloseTriggerProps()}
                    class={classes.panelClose}
                  >
                    <Icon
                      url="icon://x-circle"
                      size={20}
                      color={theme().textColor}
                    />
                  </button>
                </Show>
              </div>
            </Show>
            <div
              style={{
                display: 'flex',
                'flex-direction': 'column',
                flex: 1,
                'min-height': 0,
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  'flex-direction': 'column',
                  // grow to fill the frame when it fits; never shrink below
                  // the spec-size floor, so a clamped frame pans instead.
                  flex: '1 0 auto',
                  'min-width': '100%',
                  width: contentSize().width,
                  height: contentSize().height,
                }}
              >
                <Node
                  id={props.node.id}
                  nodeSpec={props.node}
                  nodeType={props.node.nodeType}
                  path={`/panels/#${props.id}/node`}
                />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
};

export const Eidos = (props) => {
  const { lastEvent, triggerEvent } = useEventBus();
  const [panelOpen, setPanelOpen] = createSignal([]);
  const [rootError, setRootError] = createSignal<string | null>(null);

  // EID-47: A derived node at the spec root has no source to resolve against
  // and is rejected by the renderer rather than silently rendering nothing.
  createEffect(() => {
    try {
      assertRootNotDerived(spec.root);
      setRootError(null);
    } catch (err) {
      setRootError(
        err instanceof DerivedResolutionError ? err.message : String(err),
      );
    }
  });

  const openPanel = (panel) => {
    if (panel.exclusive) {
      setPanelOpen([panel.id]);
    } else {
      setPanelOpen((prev) =>
        prev.filter((m) => m !== panel.id).concat(panel.id),
      );
    }
  };

  const closePanel = (panelid) => {
    setPanelOpen((prev) => prev.filter((m) => m !== panelid));
  };

  onMount(() => {
    // Initialise open state from spec once — event bus owns runtime state.
    if (spec.panels?.length) {
      const initially = spec.panels.filter((p) => p.open).map((p) => p.id);
      if (initially.length) setPanelOpen(initially);
    }
    triggerEvent({
      action: 'status',
      source: 'eidos',
      data: { code: 1, message: 'ready' },
    });
  });

  createEffect(
    on(lastEvent, () => {
      //console.log('Event triggered');
      if (spec.panels && spec.panels.length > 0) {
        spec.panels.forEach((panel) => {
          if (matchesAnyTrigger(lastEvent(), panel.trigger)) {
            openPanel(panel);
          }
        });
        if (lastEvent()?.action === 'openPanel' && lastEvent()?.data?.panelId) {
          const panel = spec.panels.find(
            (p) => p.id === lastEvent().data.panelId,
          );
          if (panel) openPanel(panel);
        }
        if (
          lastEvent()?.action === 'closePanel' &&
          lastEvent()?.data?.panelId
        ) {
          closePanel(lastEvent().data.panelId);
        }
      }
    }),
  );

  // Spec-driven open/close: a specPatch to panel.open drives state without
  // writing back (no feedback loop — closePanel no longer patches spec).
  // defer:true so onMount handles initial state and this only fires on changes.
  // EID-61: track only panel ids + open flags — deep-tracking spec.panels
  // re-ran this reconciliation on any patch to panel content (node subtrees,
  // titles, geometry).
  createEffect(
    on(
      () => panelsOpenSignature(spec.panels),
      () => {
        if (!spec.panels?.length) return;
        spec.panels.forEach((panel) => {
          if (panel.open && !panelOpen().includes(panel.id)) {
            openPanel(panel);
          } else if (!panel.open && panelOpen().includes(panel.id)) {
            closePanel(panel.id);
          }
        });
      },
      { defer: true },
    ),
  );

  return (
    <ThemeProvider theme={spec.theme || {}}>
      <For each={spec.panels}>
        {(panel, i) => (
          <PanelNode
            id={panel.id}
            node={panel.node}
            title={panel.title}
            width={panel.width}
            height={panel.height}
            position={panel.position}
            closable={panel.closable}
            onClose={() => closePanel(panel.id)}
            onOpen={() => openPanel(panel.id)}
            open={panelOpen().includes(panel.id)}
          />
        )}
      </For>
      <Show when={spec.title || spec.logo}>
        <HeaderBar title={spec.title} logo={spec.logo} />
      </Show>
      <Show when={rootError()}>
        <div
          role="alert"
          style={{
            padding: '1rem',
            color: '#c33',
            'font-family': 'monospace',
          }}
        >
          {rootError()}
        </div>
      </Show>
      <Show when={!rootError() && spec.root?.id} keyed>
        <div
          style={{
            width: '100%',
            height: `calc( 100% - ${spec.title || spec.logo ? 50 : 0}px )`,
            display: 'flex',
          }}
        >
          <Node
            id={spec.root.id}
            path="/root"
            nodeSpec={spec.root}
            nodeType={spec.root.nodeType}
          />
        </div>
      </Show>
    </ThemeProvider>
  );
};

// EventBridge lives inside EventBusProvider so it can call useEventBus().
// Renderer passes a signal; whenever it carries a non-null payload EventBridge
// dispatches it onto the event bus (EID-67 item 2 — triggerEvent is only
// obtainable inside the EventBusProvider subtree, not in Renderer's closure).
const EventBridge = (props: { pendingEvent: () => unknown }) => {
  const { triggerEvent } = useEventBus();
  createEffect(
    on(
      props.pendingEvent,
      (payload) => {
        if (payload !== null) triggerEvent(payload);
      },
      { defer: true },
    ),
  );
  return null;
};

export const Renderer = ({ specOrigins, id }) => {
  // EID-109: full-spec AJV validation on every settled patch (spec.ts's
  // scheduleSpecValidation) is expensive — EID-56 gated it behind
  // setSpecValidateOnPatch but nothing ever called the setter, so production
  // ran it unconditionally. Vite statically replaces import.meta.env.DEV, so
  // production bundles get `false` here (per-patch and full-spec validation
  // off) while dev/debug builds keep both on.
  setSpecValidateOnPatch(import.meta.env.DEV);
  const [auth, setAuth] = createSignal(null);
  // equals:false so identical consecutive payloads (e.g. the same primitive
  // value twice) still re-fire the EventBridge effect rather than being
  // deduplicated by the default === comparator (review finding).
  const [pendingEvent, setPendingEvent] = createSignal(null, {
    equals: false,
  });
  let wrapperRef;
  // Init handshake. Older embedding hosts (`render()` in @oceanum/eidos
  // ≤0.10.x) withhold the spec until the iframe posts `{type:'init'}`;
  // without this, the iframe mounts and waits forever ("empty renderer
  // waiting for a spec"). We can't send it just once: the runtime loads as
  // a deferred module script, so this component mounts BEFORE the iframe's
  // `load` event — but that host only attaches its message listener inside
  // `iframe.onload`, so a single early init would be missed. So we
  // re-announce on an interval until the first spec arrives, then stop.
  // Newer hosts that push the spec on iframe load (viewer.tsx) don't listen
  // for iframe→parent messages, so the extra init posts are harmless there.
  let initInterval;
  let initTimeout;
  const stopAnnouncing = () => {
    if (initInterval) {
      clearInterval(initInterval);
      initInterval = undefined;
    }
    if (initTimeout) {
      clearTimeout(initTimeout);
      initTimeout = undefined;
    }
  };
  const handleMessage = (ev) => {
    log.lazy(() => ['received message', ev.data]);
    if (!ev.data || !ev.data.type || !ev.data.id || ev.data.id != id) {
      log.lazy(() => ['ignoring message', ev.data]);
      return;
    }
    try {
      if (ev.data.type === 'spec') {
        //console.log('received spec', ev.data.payload);
        stopAnnouncing();
        specUpdate(ev.data.payload);
      } else if (ev.data.type === 'patch') {
        //console.log('received patch', ev.data.payload);
        specPatch(ev.data.payload);
      } else if (ev.data.type === 'auth') {
        //console.log('received auth', ev.data.payload);
        setAuth(ev.data.payload);
      } else if (ev.data.type === 'event') {
        // triggerEvent is only available inside EventBusProvider; dispatch via
        // EventBridge which lives in that subtree (EID-67 item 2).
        setPendingEvent(ev.data.payload);
      }
    } catch (ex) {
      console.error(ex);
    }
  };
  window.addEventListener('message', handleMessage);

  onMount(() => {
    const announce = () =>
      window.parent?.postMessage({ type: 'init', id }, '*');
    announce();
    initInterval = setInterval(announce, 150);
    // Safety stop: if no host ever answers (e.g. opened standalone), give
    // up after ~6s rather than spinning forever. Tracked so an early unmount
    // cancels it via stopAnnouncing() rather than firing as a stray no-op.
    initTimeout = setTimeout(stopAnnouncing, 6000);
  });

  onCleanup(() => {
    window.removeEventListener('message', handleMessage);
    stopAnnouncing();
  });

  return (
    // EID-86: EventBusProvider wraps DataProvider so the data layer can
    // useEventBus() (DataProvider mirrors the click event to drive transform
    // re-queries). Dependency is one-way — the bus consumes no DataContext — so
    // the swap is safe. EventBridge stays inside the bus (EID-67 inbound path).
    <EventBusProvider>
      <DataProvider
        authToken={auth}
        datameshService={DATAMESH_SERVICE}
        gatewayService={GATEWAY_SERVICE}
      >
        <EventBridge pendingEvent={pendingEvent} />
        <div
          className={classes.wrapper}
          ref={wrapperRef}
          //onContextMenu={(e) => e.preventDefault()}
        >
          <Eidos id={id} />
        </div>
      </DataProvider>
    </EventBusProvider>
  );
};
